import { useCallback, useEffect, useRef, useState } from "react";
import { useMap, useMapsLibrary } from "@vis.gl/react-google-maps";

import { hasCoords } from "./leadUtils";
import type { DealLead } from "./types";

export type DrawState = "idle" | "drawing" | "active";

/**
 * Hand-drawn polygon spatial filter (Maps JS retired DrawingManager, so we
 * draw manually): click vertices, double-click to finish, clear to reset.
 * Returns a `contains` predicate for filtering leads.
 */
export const usePolygonFilter = () => {
  const map = useMap();
  const geometry = useMapsLibrary("geometry");
  const [drawState, setDrawState] = useState<DrawState>("idle");
  // Bumped whenever the committed polygon changes so filters recompute.
  const [polygonVersion, setPolygonVersion] = useState(0);

  const polygonRef = useRef<google.maps.Polygon | null>(null);
  const previewRef = useRef<google.maps.Polygon | null>(null);
  const pointsRef = useRef<google.maps.LatLng[]>([]);
  const listenersRef = useRef<google.maps.MapsEventListener[]>([]);

  const removeListeners = () => {
    listenersRef.current.forEach((l) => l.remove());
    listenersRef.current = [];
  };

  const clear = useCallback(() => {
    removeListeners();
    previewRef.current?.setMap(null);
    previewRef.current = null;
    polygonRef.current?.setMap(null);
    polygonRef.current = null;
    pointsRef.current = [];
    map?.setOptions({ disableDoubleClickZoom: false, draggableCursor: null });
    setDrawState("idle");
    setPolygonVersion((v) => v + 1);
  }, [map]);

  const finish = useCallback(() => {
    if (pointsRef.current.length < 3) return false;
    removeListeners();
    map?.setOptions({ disableDoubleClickZoom: false, draggableCursor: null });
    polygonRef.current = previewRef.current;
    polygonRef.current?.setOptions({ fillOpacity: 0.06 });
    previewRef.current = null;
    setDrawState("active");
    setPolygonVersion((v) => v + 1);
    return true;
  }, [map]);

  const start = useCallback(() => {
    if (!map) return;
    clear();
    setDrawState("drawing");
    map.setOptions({
      disableDoubleClickZoom: true,
      draggableCursor: "crosshair",
    });
    previewRef.current = new google.maps.Polygon({
      map,
      paths: [[]],
      fillColor: "#2563eb",
      fillOpacity: 0.1,
      strokeColor: "#2563eb",
      strokeWeight: 2,
      clickable: false,
    });
    listenersRef.current.push(
      map.addListener("click", (e: google.maps.MapMouseEvent) => {
        if (!e.latLng) return;
        pointsRef.current = [...pointsRef.current, e.latLng];
        previewRef.current?.setPaths([pointsRef.current]);
      }),
      map.addListener("dblclick", () => finish()),
    );
  }, [map, clear, finish]);

  // Cycle Draw → Finish → Clear (same as the map.html button).
  const toggle = useCallback(() => {
    if (drawState === "idle") start();
    else if (drawState === "drawing") finish();
    else clear();
  }, [drawState, start, finish, clear]);

  useEffect(() => clear, [clear]); // teardown on unmount/map change

  const contains = useCallback(
    (lead: DealLead): boolean => {
      const polygon = polygonRef.current;
      if (!polygon || !geometry) return true;
      if (!hasCoords(lead)) return false; // no coords → can't be inside
      return geometry.poly.containsLocation(
        new google.maps.LatLng(lead.lat, lead.lng),
        polygon,
      );
    },
    // polygonVersion re-creates the predicate when the polygon changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [geometry, polygonVersion],
  );

  return { drawState, toggle, contains, hasPolygon: drawState === "active" };
};
